import { writable } from 'svelte/store';

export interface Position {
	x: number;
	y: number;
	timestamp: number;
}

export interface RadarDotData {
	id: string;
	currentPosition: Position;
	previousPosition?: Position;
	trail: Position[];
	isActive: boolean;
	color: string;
	createdAt: number; // Timestamp when dot was first created
	pendingTrailUpdate?: NodeJS.Timeout; // Timer for delayed trail updates
}

// Configuration
const TRAIL_LENGTH = 0; // Trails removed — no ghost circles rendered
const TRAIL_FADE_DURATION = 450; // ms — kept for inactive-dot cleanup timing
const POSITION_THRESHOLD = 1; // px — minimum movement to add a trail point
const TRAIL_DELAY = 1000; // ms — delay before trails start appearing for new dots
export const ANIMATION_DURATION = 400; // ms — must match the tween duration in Dot.svelte

class RadarPositionManager {
	private dotData = new Map<string, RadarDotData>();
	private store = writable<Map<string, RadarDotData>>(new Map());
	private cleanupTimeout: ReturnType<typeof setTimeout> | undefined;

	// Update a single dot's internal state without flushing the store.
	// Used by batchUpdatePositions to avoid N store.set calls per message.
	private applyPositionUpdate(id: string, x: number, y: number, color: string) {
		const currentTime = Date.now();
		const newPosition: Position = { x, y, timestamp: currentTime };

		const existingData = this.dotData.get(id);

		if (existingData) {
			const prevPos = existingData.currentPosition;
			const distance = Math.sqrt(
				Math.pow(newPosition.x - prevPos.x, 2) + Math.pow(newPosition.y - prevPos.y, 2),
			);

			if (distance >= POSITION_THRESHOLD) {
				const timeSinceCreation = currentTime - existingData.createdAt;
				if (timeSinceCreation >= TRAIL_DELAY) {
					if (existingData.pendingTrailUpdate) {
						clearTimeout(existingData.pendingTrailUpdate);
					}

					const positionToAdd = { ...prevPos };

					existingData.pendingTrailUpdate = setTimeout(() => {
						existingData.trail.unshift(positionToAdd);

						if (existingData.trail.length > TRAIL_LENGTH) {
							existingData.trail = existingData.trail.slice(0, TRAIL_LENGTH);
						}

						existingData.pendingTrailUpdate = undefined;
						this.store.set(new Map(this.dotData));
					}, ANIMATION_DURATION);
				}

				existingData.previousPosition = prevPos;
				existingData.currentPosition = newPosition;
			} else {
				existingData.currentPosition.timestamp = currentTime;
			}

			existingData.isActive = true;
			existingData.color = color;
		} else {
			this.dotData.set(id, {
				id,
				currentPosition: newPosition,
				trail: [],
				isActive: true,
				color,
				createdAt: currentTime,
			});
		}
	}

	// Batch-update all points from one radar frame and flush the store once.
	batchUpdatePositions(points: { id: string; x: number; y: number; color: string }[]) {
		for (const pt of points) {
			this.applyPositionUpdate(pt.id, pt.x, pt.y, pt.color);
		}
		this.cleanupOldTrailPoints();
		this.store.set(new Map(this.dotData));
	}

	// Mark dots as inactive (when they're no longer being tracked)
	markInactive(activeIds: string[]) {
		const activeIdSet = new Set(activeIds);

		for (const [, data] of this.dotData) {
			if (!activeIdSet.has(data.id)) {
				data.isActive = false;
				if (data.pendingTrailUpdate) {
					clearTimeout(data.pendingTrailUpdate);
					data.pendingTrailUpdate = undefined;
				}
			}
		}

		// Cancel any previous cleanup timeout to avoid stacking
		if (this.cleanupTimeout !== undefined) {
			clearTimeout(this.cleanupTimeout);
		}

		this.cleanupTimeout = setTimeout(() => {
			this.cleanupTimeout = undefined;
			for (const [id, data] of this.dotData) {
				if (!data.isActive) {
					if (data.pendingTrailUpdate) {
						clearTimeout(data.pendingTrailUpdate);
					}
					this.dotData.delete(id);
				}
			}
			this.store.set(new Map(this.dotData));
		}, 1000);

		this.store.set(new Map(this.dotData));
	}

	// Clean up old trail points
	private cleanupOldTrailPoints() {
		const currentTime = Date.now();

		for (const data of this.dotData.values()) {
			if (data.isActive) {
				// For active dots, keep all trail points regardless of age
				// Only limit by TRAIL_LENGTH to prevent memory issues
				if (data.trail.length > TRAIL_LENGTH) {
					data.trail = data.trail.slice(0, TRAIL_LENGTH);
				}
			} else {
				// For inactive dots, clean up trail points by age to fade them out
				data.trail = data.trail.filter(
					(point) => currentTime - point.timestamp < TRAIL_FADE_DURATION,
				);
			}
		}
	}

	// Get the store for subscribing
	subscribe(callback: (value: Map<string, RadarDotData>) => void) {
		return this.store.subscribe(callback);
	}

	// Clear all data
	clear() {
		// Clear all pending timeouts before clearing data
		for (const data of this.dotData.values()) {
			if (data.pendingTrailUpdate) {
				clearTimeout(data.pendingTrailUpdate);
			}
		}
		this.dotData.clear();
		this.store.set(new Map());
	}

	// Get specific dot data
	getDotData(id: string): RadarDotData | undefined {
		return this.dotData.get(id);
	}
}

export const radarPositionManager = new RadarPositionManager();
